/*
 * @providesModule LeagueToastUtils
 * Utilities for League Toast
 */

import {GetLeaguePlayersResult, getLeaguePlayers} from '@api/GetLeaguePlayers';
import {GetLeaguesResult, getLeagues} from '@api/GetLeagues';
import {League, LeagueType} from '@api/Leagues';
import AsyncStorage from '@react-native-async-storage/async-storage';
import ClientPropertyConstants from 'ClientPropertyConstants';
import GlobalEventEmitter from 'GlobalEventEmitter';
import {hasUserBeenInstantPromoted, isProgressiveLeague} from 'LeagueInfoUtils';
import {Platform} from 'react-native';

const LEAGUE_PLACEMENT = 'LEAGUE_PLACEMENT';
const EVENT_EMIT_TIMER = Platform.OS === 'ios' ? 2000 : 1250;
const SIGNIFICANT_RANK_DOWN_AMOUNT = 5; // Product decision, change this number if Product wants to change again

export type LeaguePlaceData = {
  label: string;
  place: number | undefined;
  id: string;
  league?: League; // Only used for Progressive Leagues
};

export type LeaguePlaceResult = {
  label: string;
  start: number;
  end: number;
  hasBeenInstantPromoted: boolean; // Only used for Progressive Leagues
  league: League | undefined; // Only used for Progressive Leagues
};

type ProgressiveLeagueZone = 'PROMOTION' | 'SAFE' | 'DEMOTION';

/**
 * Fetches current player league position and stores in ASYNC Storage
 */
export const writeLeaguePlacement = async (type: LeagueType) => {
  if (Platform.OS === 'web') {
    // Leagues not implemented on Web.
    return;
  }
  // Get current leagues player is in
  const leaguesResult: GetLeaguesResult = await getLeagues({});

  // If leagues have not been unlocked, do not write data
  if (!leaguesResult?.leagues?.filter((league) => league.games_to_unlock === 0).length) {
    console.debug(`LeagueToast: no data - leagues not unlocked yet`);
    clearStoredToastData();
    return;
  }

  // Get league that matches the passed in type
  const currentLeague = leaguesResult?.leagues?.filter((league) => league.type === type)[0];
  const instId = currentLeague?.league_instance_id;

  if (currentLeague && instId) {
    const playerResult: GetLeaguePlayersResult = await getLeaguePlayers({
      league_instance_id: instId,
      limit: 1,
    });

    const data: string = JSON.stringify({
      label: currentLeague.label,
      place: playerResult.current_player?.place,
      id: instId,
      league: currentLeague,
    });

    console.debug(`LeagueToast: Writing league data: ${data}`);
    // Write data to storage for each league type
    AsyncStorage.setItem(LEAGUE_PLACEMENT, data);
  }
};

export const showToast = async () => {
  if (ClientPropertyConstants.getLeaguesToastEnabled()) {
    const leaguePlace = await getLeaguePlacement();
    const diff = leaguePlace.end - leaguePlace.start;
    const hasRankedUp = diff < 0;
    const hasRankedDown = diff > 0;
    const hasRankedDownSignificantly = diff >= SIGNIFICANT_RANK_DOWN_AMOUNT;
    const hasMovedDownZone = hasPlayerMovedDownZone(leaguePlace);
    const eligibleForRankDownToast =
      hasRankedDown && (hasRankedDownSignificantly || hasMovedDownZone);

    const shouldShowRankDownToast =
      eligibleForRankDownToast && ClientPropertyConstants.getLeaguesNegativeToastEnabled();

    const shouldEmit = hasRankedUp || shouldShowRankDownToast || leaguePlace.hasBeenInstantPromoted;

    // Clear out the data
    clearStoredToastData();

    if (shouldEmit && leaguePlace) {
      setTimeout(() => {
        GlobalEventEmitter.emit('showLeagueToast', leaguePlace);
      }, EVENT_EMIT_TIMER);
    }
  }
};

/**
 * Returns difference of player's league place and if the player has been instant promoted (only
 * used if the league is a Progressive League).
 * @param type The league type to fetch place for
 */
const getLeaguePlacement = async (): Promise<LeaguePlaceResult> => {
  let result: LeaguePlaceResult = {
    label: '',
    start: 0,
    end: 0,
    hasBeenInstantPromoted: false,
    league: undefined,
  };

  // Get last saved placement data from storage for this league type
  const storedString = await AsyncStorage.getItem(LEAGUE_PLACEMENT);

  if (storedString) {
    const data: LeaguePlaceData = JSON.parse(storedString);

    // NOTE: Checking for data?.place here ensures that if player has 0 medals to start, we will not show toast
    if (data?.id && data?.place && data?.league) {
      const playerResult: GetLeaguePlayersResult = await getLeaguePlayers({
        league_instance_id: data.id,
        limit: 1,
      });
      if (playerResult?.current_player) {
        result = {
          end: playerResult.current_player.place,
          start: data.place,
          label: data.label,
          hasBeenInstantPromoted: false,
          league: data.league,
        };
      }
      // If the league is a Progressive League, need to fetch the current League and check if the
      // user has been Instant Promoted
      if (isProgressiveLeague(data.league)) {
        const leaguesResult: GetLeaguesResult = await getLeagues({});
        // Per Server contract: if we are using Progressive Leagues, there will be only one League
        // in the response
        const currentLeague = leaguesResult?.leagues[0];
        if (currentLeague) {
          // Check diff between old and current league, write result to hasBeenInstantPromoted
          result.hasBeenInstantPromoted = hasUserBeenInstantPromoted(data.league, currentLeague);
          // Write current league to result
          result.league = currentLeague;
        }
      }
    }
  }
  console.debug(`LeagueToast: League Place Result: ${JSON.stringify(result)}`);
  return result;
};

/*
 *  Clear out any stored league placement data
 */
export const clearStoredToastData = async () => {
  const data: LeaguePlaceData = {
    label: '',
    id: '',
    place: 0,
    league: undefined,
  };
  AsyncStorage.setItem(LEAGUE_PLACEMENT, JSON.stringify(data));
};

/**
 * Checks the LeaguePlaceResult information to see if the player has moved down a zone during the time
 * when the two leaguePlace positions were checked.
 * @param leaguePlace LeaguePlaceResult created by getLeaguePlacement()
 * @returns boolean: true if player has moved down a zone, false if not
 */
export const hasPlayerMovedDownZone = (leaguePlace: LeaguePlaceResult) => {
  const previousPlace = leaguePlace.start;
  const currentPlace = leaguePlace.end;
  const diff = currentPlace - previousPlace;

  // Before checking for zones, these are cases where we know player has not moved down a zone
  if (
    diff <= 0 ||
    leaguePlace.hasBeenInstantPromoted ||
    !leaguePlace.league ||
    !isProgressiveLeague(leaguePlace.league)
  ) {
    return false;
  }

  // Get previous zone and current zone
  const playerPreviousZone = getZoneByLeagueAndPlace(leaguePlace.league, previousPlace);
  const playerCurrentZone = getZoneByLeagueAndPlace(leaguePlace.league, currentPlace);

  // If player was in promotion but now isn't, they've moved down
  if (playerPreviousZone === 'PROMOTION' && playerCurrentZone !== 'PROMOTION') {
    return true;
  }

  // If player was in safe but now is demotion, they've moved down
  if (playerPreviousZone === 'SAFE' && playerCurrentZone === 'DEMOTION') {
    return true;
  }

  // Anything else means the player has not moved down
  return false;
};

/**
 * Util function that returns the player's zone, given league and place information
 * @param league league to check for promotion, safe, and demotion zones
 * @param place place in the leaderboard
 * @returns ProgressiveLeagueZone
 */
export const getZoneByLeagueAndPlace = (league: League, place: number): ProgressiveLeagueZone => {
  const promotionZonePosition = league.progressive_league?.season_promotion_position || 0;
  const demotionZonePosition = league.progressive_league?.season_demotion_position || 0;

  // This would only occur if the league was not progressive, or there was only 1 progressive league
  // configured for the entire set. OK to return SAFE here, as it will do so for both current and previous
  // places, and hasPlayerMovedDown will always return false
  if (promotionZonePosition === 0 && demotionZonePosition === 0) {
    return 'SAFE';
  }

  // First check promotion zone: if promotion slots exist, everyone at promotionZonePosition or above
  // is in the promotion zone.
  if (place <= promotionZonePosition) {
    return 'PROMOTION';
  }

  // Next check demotion zone: if demotion slots exist, everyone at demotionZonePosition and below
  // is in the demotion zone.
  if (demotionZonePosition !== 0 && place >= demotionZonePosition) {
    return 'DEMOTION';
  }

  // Everyone else is in the safe zone.
  return 'SAFE';
};
